import { logError } from "src/errorHandling/errorLogging.ts";
import i18n from "i18next";
import type { CustomAppAccess } from "src/api/generated/erp/customApps/runtime/customAppAccess.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import type { AgentUiScopeContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import type { CustomAppHostBridge } from "src/customApps/runtime/customAppHostBridge.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { CustomAppFrame } from "./CustomAppFrame.tsx";

export interface OpenCustomAppFrameDialogParams {
	access: CustomAppAccess;
	customAppId: number;
	dialogTitle: string;
	appName: string;
	agentContext: AgentUiScopeContext;
	onClosed?: () => Promise<unknown>;
}

/** Opens a Custom App in a fullscreen dialog that lets the app confirm closing with unsaved changes. */
export function useOpenCustomAppFrameDialog() {
	const { openDialog } = useGenericDialog();
	const { userPermissions } = useGlobalInitData();
	return ({ access, customAppId, dialogTitle, appName, agentContext, onClosed }: OpenCustomAppFrameDialogParams) => {
		let bridge: CustomAppHostBridge | undefined;
		openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
			title: dialogTitle,
			size: "fullscreen",
			agentContext,
			onBeforeClose: async () => {
				const decision = await bridge?.beforeClose();
				const warning = decision?.unresponsive ? i18n.t("custom_app_close_unresponsive") : decision?.warning;
				onDataDirtyStateChanged({
					isDirty: Boolean(warning),
					confirmCloseWhileDirtyMessage: warning,
				});
			},
			onClose: async () => {
				await bridge?.notifyClosed();
				bridge?.dispose();
				await onClosed?.().catch(logError);
			},
			content: (
				<CustomAppFrame
					{...access}
					viewerPermissions={userPermissions}
					customAppId={customAppId}
					title={appName}
					onRequestClose={() => {
						void closeDialog({ confirmIfEdited: true }).catch(logError);
					}}
					onBridge={(value) => {
						bridge = value;
					}}
				/>
			),
		}));
	};
}
